import React, { useState } from 'react';
import { Input, Label, Checkbox } from '../input/Input';
import { Button } from '../button/Button';

export function LoginForm({ onSubmit, onForgotPassword }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.({ email, password, remember });
  };

  return (
    <div className="w-full max-w-[400px] bg-white border border-border p-8 rounded-card shadow-md font-body space-y-6">
      {/* Brand Header */}
      <div className="text-center">
        <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-white font-display font-semibold text-2xl mx-auto mb-3">S</div>
        <h2 className="font-display text-h3 font-bold text-text-primary">SON</h2>
        <p className="text-caption text-text-secondary mt-1">Conecte-se para gerenciar seus negócios.</p>
      </div>

      {/* Main Login Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="email" required>Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="nome@empresa.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-1.5">
            <Label htmlFor="password" required>Senha</Label>
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-caption font-semibold text-primary hover:underline"
            >
              Esqueci minha senha
            </button>
          </div>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <Checkbox
          id="remember"
          label="Permanecer conectado"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
        />

        <Button type="submit" className="w-full mt-2">
          Entrar
        </Button>
      </form>

      {/* Social login divider */}
      <div className="flex items-center my-4">
        <div className="flex-grow border-t border-border" />
        <span className="px-3 text-caption text-text-secondary uppercase font-semibold tracking-wider">ou continue com</span>
        <div className="flex-grow border-t border-border" />
      </div>

      {/* SSO Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="secondary" size="sm" type="button" className="w-full flex items-center justify-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.24 10.285V13.4h6.887c-.275 1.565-1.88 4.604-6.887 4.604-4.33 0-7.859-3.578-7.859-8s3.53-8 7.859-8c2.46 0 4.105 1.025 5.047 1.926l2.427-2.334C17.955 2.192 15.34 1 12.24 1 5.92 1 1 5.92 1 12s4.92 11 11.24 11c6.59 0 11-4.64 11-11.2 0-.753-.08-1.32-.178-1.815H12.24z" />
          </svg>
          Google
        </Button>
        <Button variant="secondary" size="sm" type="button" className="w-full flex items-center justify-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 23 23" fill="currentColor">
            <path d="M0 0h11v11H0z" fill="#f25022" /><path d="M12 0h11v11H12z" fill="#7fba00" /><path d="M0 12h11v11H0z" fill="#00a4ef" /><path d="M12 12h11v11H12z" fill="#ffb900" />
          </svg>
          Microsoft
        </Button>
      </div>
    </div>
  );
}

export function MfaVerification({ onVerify, onCancel }) {
  const [code, setCode] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length === 6) {
      onVerify?.(code);
    }
  };

  return (
    <div className="w-full max-w-[400px] bg-white border border-border p-8 rounded-card shadow-md font-body space-y-6">
      <div className="text-center">
        <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center text-primary font-bold text-2xl mx-auto mb-3">🛡️</div>
        <h2 className="font-display text-h3 font-bold text-text-primary">MFA Ativado</h2>
        <p className="text-caption text-text-secondary mt-1">Digite o código de verificação enviado para seu dispositivo.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="code">Código de Verificação</Label>
          <Input
            id="code"
            type="text"
            placeholder="123456"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="text-center tracking-[0.5em] text-h4 font-bold font-mono"
          />
          <span className="text-caption text-text-secondary mt-1 block text-center">Insira o código de 6 dígitos.</span>
        </div>

        <Button type="submit" disabled={code.length !== 6} className="w-full mt-2">
          Verificar
        </Button>

        {onCancel && (
          <Button variant="tertiary" type="button" onClick={onCancel} className="w-full">
            Voltar
          </Button>
        )}
      </form>
    </div>
  );
}

export function ActiveSessions({ initialSessions = [] }) {
  const [sessions, setSessions] = useState(initialSessions.length > 0 ? initialSessions : [
    { id: 1, os: 'Windows', browser: 'Chrome', location: 'São Paulo', activeTime: 'Ontem', current: false },
    { id: 2, os: 'macOS', browser: 'Safari', location: 'Rio de Janeiro', activeTime: 'Agora mesmo', current: true }
  ]);

  const handleEndSession = (id) => {
    setSessions(sessions.filter(s => s.id !== id));
  };

  return (
    <div className="bg-white border border-border p-6 rounded-card shadow-sm font-body space-y-6">
      <div>
        <h3 className="text-body font-bold text-text-primary uppercase tracking-wide">Sessões Ativas</h3>
        <p className="text-caption text-text-secondary mt-1">Gerencie os dispositivos conectados à sua conta SON.</p>
      </div>

      <div className="divide-y divide-border">
        {sessions.map((session) => (
          <div key={session.id} className="py-4 flex justify-between items-center first:pt-0 last:pb-0">
            <div className="flex gap-3 items-center">
              <div className="w-10 h-10 rounded-button bg-background flex items-center justify-center text-lg text-text-secondary">
                {session.os === 'Windows' ? '💻' : '📱'}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-small font-bold text-text-primary">{session.os} · {session.browser}</p>
                  {session.current && (
                    <span className="text-caption px-2 py-0.5 bg-success/15 text-success rounded-full font-semibold">Esta sessão</span>
                  )}
                </div>
                <p className="text-caption text-text-secondary">{session.location} · {session.activeTime}</p>
              </div>
            </div>

            {!session.current && (
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => handleEndSession(session.id)}
                className="text-danger hover:bg-danger/5 hover:text-danger font-semibold"
              >
                Encerrar
              </Button>
            )}
          </div>
        ))}
        {sessions.length === 0 && (
          <div className="py-6 text-center text-caption text-text-secondary">
            Nenhuma outra sessão ativa encontrada.
          </div>
        )}
      </div>
    </div>
  );
}
